import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  alpha,
  useTheme
} from '@mui/material';

interface StatsCardProps {
  title: string;
  value: string;
  change: string;
  color: 'primary' | 'success' | 'warning' | 'info' | 'error';
  icon: React.ReactNode;
  isPositive?: boolean;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  change,
  color,
  icon,
  isPositive = true
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        border: `1px solid ${alpha(theme.palette[color].main, 0.08)}`,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg,
            ${theme.palette[color].main} 0%,
            ${theme.palette[color].light} 100%)`,
        }
      }}
    >
      <CardContent sx={{ position: 'relative', p: 3 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h3"
              component="div"
              className="stats-value"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg,
                  ${theme.palette[color].main} 0%,
                  ${theme.palette[color].dark} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 2,
                transition: 'all 0.3s ease'
              }}
            >
              {value}
            </Typography>
            <Chip
              label={change}
              size="small"
              sx={{
                background: isPositive
                  ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                color: isPositive ? theme.palette.success.main : theme.palette.error.main,
                border: `1px solid ${alpha(isPositive ? theme.palette.success.main : theme.palette.error.main, 0.2)}`,
                fontWeight: 600,
                fontSize: '0.75rem',
                '& .MuiChip-label': {
                  px: 2
                }
              }}
            />
          </Box>
          <Box
            className="stats-icon"
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              background: `linear-gradient(135deg,
                ${alpha(theme.palette[color].main, 0.1)} 0%,
                ${alpha(theme.palette[color].main, 0.05)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette[color].main,
              fontSize: '24px',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette[color].main, 0.1)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {icon}
          </Box>
        </Box>

        {/* Subtle background pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: `radial-gradient(circle,
              ${alpha(theme.palette[color].main, 0.03)} 0%,
              transparent 70%)`,
            pointerEvents: 'none'
          }}
        />
      </CardContent>
    </Card>
  );
};

export default StatsCard;
